import React, { Suspense, lazy, useEffect } from 'react';
import ScrollToButtons from './components/ScrollToButtons';
import Footer from './components/Footer';
import { Routes, Route, useParams, useLocation } from 'react-router-dom';
import UserProvider from './components/UserProvider';
import ToastProvider from './components/Toast';
import PasswordReset from './components/PasswordReset';
import EmailVerification from './components/EmailVerification';
import AdminDashboard from './components/AdminDashboard';
import AdminPasswordModal from './components/AdminPasswordModal';
import useAdminVerification from './hooks/useAdminVerification';
import { setPageTitle, PAGE_TITLES } from './utils/pageTitle';
import './css.css'
import Main from './Main';

// require.context를 사용하여 pages 디렉토리의 모든 페이지를 동적으로 가져옵니다.
const requirePage = require.context('./pages', true, /\.js$/);

const pages = requirePage.keys().reduce((acc, path) => {
  const pageName = path.match(/\.\/(.*)\.js$/)[1].toLowerCase();
  acc[pageName] = lazy(() => import(`./pages/${pageName}`));
  return acc;
}, {});

// 페이지 방문 통계 기록 함수
const recordPageVisit = async (pageName) => {
  try {
    // dev.rf4kr.com 서브도메인인 경우 통계 기록하지 않음
    if (window.location.hostname === 'dev.rf4kr.com') {
      console.log('개발 환경에서는 페이지 방문 통계를 기록하지 않습니다.');
      return;
    }
    
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch('/api/page-visit', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ pageName })
    });
  } catch (error) {
    // 통계 기록 실패는 조용히 처리 (사용자 경험에 영향 없음)
    console.warn('페이지 방문 통계 기록 실패:', error);
  }
};

// 페이지 방문 추적 컴포넌트
const PageVisitTracker = () => {
  const location = useLocation();

  useEffect(() => {
    let pageName = 'home'; // 기본값

    if (location.pathname === '/') {
      pageName = 'home';
    } else if (location.pathname.startsWith('/pages/')) {
      pageName = location.pathname.replace('/pages/', '');
    } else if (location.pathname === '/admin') {
      pageName = 'admin';
    } else if (location.pathname === '/reset-password') {
      pageName = 'reset-password';
    } else if (location.pathname === '/verify-email') {
      pageName = 'verify-email';
    } else {
      pageName = location.pathname.replace('/', '') || 'unknown';
    }

    // 페이지 제목 설정
    const pageTitle = PAGE_TITLES[pageName];
    setPageTitle(pageTitle);

    // 페이지 방문 기록
    recordPageVisit(pageName);
  }, [location.pathname]);

  return null; // 렌더링할 내용 없음
};

const Page = () => {
  const { id } = useParams();
  const PageComponent = pages[id.toLowerCase()];

  useEffect(() => {
    if (!PageComponent) {
      // 페이지가 존재하지 않으면 404 에러 페이지로 리다이렉트
      window.location.href = '/error/404.html';
    }
  }, [PageComponent]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {PageComponent ? <PageComponent /> : <div>Redirecting to error page...</div>}
    </Suspense>
  );
};

// 관리자 인증 모달을 관리하는 컴포넌트 (UserProvider 내부에서 사용)
const AdminAuthManager = () => {
  const { 
    showPasswordModal, 
    setShowPasswordModal, 
    handlePasswordSuccess 
  } = useAdminVerification();

  return (
    <AdminPasswordModal
      isOpen={showPasswordModal}
      onClose={() => setShowPasswordModal(false)}
      onSuccess={handlePasswordSuccess}
    />
  );
};

function App() {
  return (
    <ToastProvider>
      <UserProvider>
        <PageVisitTracker />
        <Main />
        <div className='main-app-content'>
          <Routes>
            <Route path="/" element={<div />} />
            <Route path="/pages/:id" element={<Page />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <ScrollToButtons />
        </div>
        <Footer />
        
        {/* 전역 관리자 암호 인증 모달 */}
        <AdminAuthManager />
      </UserProvider>
    </ToastProvider>
  );
}

export default App;